import type { i18n as I18n } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldSpec } from 'v5/core/entity-schema/entitySchema.types';
import type { ValidationIssue } from 'v5/core/entity-values/values.types';
import { createPartIssueMessage } from './partIssueMessage';

export type IssueMessage = (issue: ValidationIssue, spec: FieldSpec) => string;

/**
 * A resource field's issue in the language `i18n` speaks at the time. An
 * issue about one part of a field (a tag's name, a rate's fee) reads as that
 * kind's own sentence (`createPartIssueMessage`); the rest name the field by
 * its label.
 */
export function createResourceIssueMessage(i18n: I18n): IssueMessage {
  const partMessage = createPartIssueMessage(i18n);
  const t = i18n.getFixedT(null, 'patterns', 'SchemaForm.validation');
  function genericMessage(issue: ValidationIssue, spec: FieldSpec): string {
    const label = spec.label;
    switch (issue.code) {
      case 'required':
      case 'part-required':
        return t('required', { label });
      case 'not-a-number':
        return t('notANumber', { label });
      case 'whole-number':
        return t('wholeNumber', { label });
      case 'whole-inches':
        return t('wholeInches', { label });
      case 'out-of-range':
        return t('outOfRange', { label, min: issue.min, max: issue.max });
      case 'invalid-option':
        return t('invalidOption', { label });
      case 'invalid-project':
        return t('invalidProject', { label });
      case 'invalid-date':
        return t('invalidDate', { label });
      case 'invalid-date-range':
        return t('invalidDateRange', { label });
      case 'date-range-order':
        return t('dateRangeOrder', { label });
      case 'invalid-url':
        return t('invalidUrl');
      case 'invalid-email':
        return t('invalidEmail');
      case 'tags-not-allowed':
        return t('tagsNotAllowed');
      case 'invalid-amount':
        return t('invalidAmount');
      case 'negative-amount':
        return t('negativeAmount');
      case 'no-decimals':
        return t('noDecimals', { currency: issue.currency });
      case 'too-many-decimals':
        return t('tooManyDecimals', { count: issue.max });
      case 'invalid-fee':
      case 'fee-whole-percent':
        return t('invalidPercent', { label });
      case 'billing-unit':
        return t('billingUnit');
    }
  }
  return (issue, spec) =>
    partMessage(spec.kind, issue) ?? genericMessage(issue, spec);
}

/** `createResourceIssueMessage` over the i18n instance of the surrounding provider. */
export function useResourceIssueMessage(): IssueMessage {
  const { i18n } = useTranslation();
  return useMemo(() => createResourceIssueMessage(i18n), [i18n]);
}
